import { Badge } from '@reflex/ui/components/ui/badge';
import { cn } from '@reflex/ui/lib/utils';

interface BadgeConfig {
  label: string;
  className: string;
  dot?: string;
}

interface ConfigBadgeProps {
  config: Record<string, BadgeConfig>;
  value: string;
  fallbackLabel?: string;
  /** Render as a minimal dot + label instead of a bordered badge. */
  variant?: 'badge' | 'inline';
}

/**
 * Generic badge that looks up styling from a config map by value.
 * Falls back to a plain outline badge for unknown values.
 *
 * - `variant="badge"` (default): bordered pill badge
 * - `variant="inline"`: minimal dot + label, no border — for dense header bars
 */
export function ConfigBadge({ config, value, fallbackLabel, variant = 'badge' }: ConfigBadgeProps) {
  const cfg = config[value];

  if (variant === 'inline') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <span
          className={cn('h-1.5 w-1.5 rounded-full shrink-0', cfg?.dot ?? 'bg-status-neutral')}
        />
        {cfg?.label ?? fallbackLabel ?? value}
      </span>
    );
  }

  if (!cfg) {
    return (
      <Badge variant="outline" className="text-3xs px-1.5 py-0 font-medium">
        {fallbackLabel ?? value}
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className={cn('text-3xs px-1.5 py-0 font-medium tracking-wide', cfg.className)}
    >
      {cfg.label}
    </Badge>
  );
}
